import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAlbumContext } from "@/contexts/AlbumProvider";
export type RankingRow = {
    user_id: string;
    total_votes: number;
    voted_photos_count: number;
    display_name: string | null;
    avatar_url: string | null;
};

export function useRanking() {
    const [data, setData] = useState<RankingRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<any>(null);
    const { albumId } = useAlbumContext()

    const abortRef = useRef<AbortController | null>(null);
    const prevDataRef = useRef<string>("");

    useEffect(() => {
        let cancelled = false;
        let intervalMs = 5000;
        const maxBackoff = 30000;

        async function poll() {
            while (!cancelled) {
                try {
                    if (document.hidden) {
                        await sleep(1000);
                        continue;
                    }

                    // cancelar request previa si sigue viva
                    abortRef.current?.abort();
                    const controller = new AbortController();
                    abortRef.current = controller;

                    const { data: rows, error } = await supabase.rpc(
                        "get_leaderboard_top10",
                        { p_album_id: albumId ?? null }
                    );

                    if (error) throw error;

                    const serialized = JSON.stringify(rows ?? []);
                    if (serialized !== prevDataRef.current) {
                        prevDataRef.current = serialized;
                        if (!cancelled) setData(rows ?? []);
                    }

                    setError(null);
                    setLoading(false);
                    intervalMs = 5000; // reset backoff
                } catch (err: any) {
                    if (err.name === "AbortError") {
                        // request cancelada, no es un error real
                    } else {
                        console.error("Leaderboard poll error:", err);
                        setError(err);
                        intervalMs = Math.min(intervalMs * 2, maxBackoff); // backoff
                    }
                }

                // jitter ±500ms
                const jitter = 250 + Math.random() * 500;
                await sleep(intervalMs + jitter);
            }
        }

        poll();

        return () => {
            cancelled = true;
            abortRef.current?.abort();
        };
    }, [albumId]);

    return { data, loading, error };
}

function sleep(ms: number) {
    return new Promise((res) => setTimeout(res, ms));
}